import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import { BufferAttribute, BufferGeometry, Color, DoubleSide, MeshLambertMaterial } from 'three';
import { PRAYERS, type Building, type RegionTree } from '@map/shared';
import {
  add,
  containsPoint,
  cross,
  dot,
  normalise,
  scale,
  slerp,
  sub,
  type Territory,
  type Vec3,
} from '@map/worldkit';
import { prefersReducedMotion } from '../styles/tokens.js';

/**
 * The five prayers as one river, rising in Fajr's country and running through
 * each prayer's in turn to 'Isha. "If there were a river at your door and you
 * bathed in it five times a day, would any dirt be left on you?"
 *
 * Each stretch shows how its prayer is kept: clear and quick when kept, then
 * slowing and silting to mud, and at worst a still, green, poisoned water
 * that bubbles. Where one prayer's country gives way to the next, the water
 * blends from one state to the other. Nothing here burns: this replaces the
 * towers and their smoke, and reads the same weathering they did.
 */
export interface SalahRiverProps {
  territories: Map<string, Territory>;
  tree: RegionTree;
  buildings: Building[];
}

/** Land is drawn at 1.02; the water sits a hair above it, its banks under that. */
const WATER_RADIUS = 1.0209;
const BANK_RADIUS = 1.0206;
/** Half the river's width at the mouth; it rises from a thinner spring. */
const HALF_WIDTH = 0.0095;
const BANK_MARGIN = 1.6;
const SAMPLES_PER_STRETCH = 48;

const BANK = new Color('#7a6a4c');

interface Stop {
  site: Vec3;
  weathering: number;
}

export function SalahRiver({ territories, tree, buildings }: SalahRiverProps) {
  // In prayer order, spring to mouth: the countries themselves are laid out
  // that way (see salahOrder.ts), so the order the day runs in and the order
  // the water runs in are the same thing.
  const course = useMemo(() => {
    const byPrayer = new Map(
      buildings.filter((b) => b.kind === 'salah' && b.prayer).map((b) => [b.prayer, b] as const),
    );
    const stops: Stop[] = [];
    let continent: Territory | undefined;
    for (const { key } of PRAYERS) {
      const building = byPrayer.get(key);
      const territory = building && territories.get(building.regionId);
      if (!building || !territory || !tree.regions[building.regionId]) continue;
      stops.push({ site: normalise(territory.site), weathering: building.weathering });
      continent ??= territory.parentId ? territories.get(territory.parentId) : undefined;
    }
    if (stops.length < 2) return null;
    return traceCourse(stops, continent);
  }, [buildings, territories, tree]);

  const geometries = useMemo(() => {
    if (!course) return null;
    return {
      water: ribbon(course, WATER_RADIUS, 1),
      bank: ribbon(course, BANK_RADIUS, BANK_MARGIN),
    };
  }, [course]);
  useEffect(
    () => () => {
      geometries?.water.dispose();
      geometries?.bank.dispose();
    },
    [geometries],
  );

  const water = useMemo(() => riverMaterial(), []);
  useEffect(() => () => water.material.dispose(), [water]);

  const still = useMemo(() => prefersReducedMotion(), []);
  useFrame((_, delta) => {
    if (!still) water.time.value += delta;
  });

  if (!geometries) return null;
  return (
    <group>
      <mesh geometry={geometries.bank}>
        <meshLambertMaterial color={BANK} side={DoubleSide} />
      </mesh>
      <mesh geometry={geometries.water} material={water.material} />
    </group>
  );
}

interface CoursePoint {
  at: Vec3;
  weathering: number;
  /** 0 at the spring, 1 at the mouth. */
  along: number;
}

/**
 * Through every prayer's country, bending a little between them so
 * it reads as a river and not a route, and held inside the continent so it
 * never runs out over the sea.
 */
function traceCourse(stops: Stop[], continent: Territory | undefined): CoursePoint[] {
  // A bend between each pair of countries, alternating side.
  const controls: Array<{ at: Vec3; weathering: number }> = [];
  stops.forEach((stop, i) => {
    controls.push({ at: stop.site, weathering: stop.weathering });
    const next = stops[i + 1];
    if (!next) return;
    const mid = slerp(stop.site, next.site, 0.5);
    const side = normalise(cross(mid, sub(next.site, stop.site)));
    const reach = Math.sqrt(dot(sub(next.site, stop.site), sub(next.site, stop.site)));
    controls.push({
      at: normalise(add(mid, scale(side, reach * 0.22 * (i % 2 === 0 ? 1 : -1)))),
      weathering: (stop.weathering + next.weathering) / 2,
    });
  });

  // Catmull-Rom through the controls, then back onto the sphere.
  const points: CoursePoint[] = [];
  const last = controls.length - 1;
  const at = (k: number) => (controls[Math.min(last, Math.max(0, k))] as { at: Vec3 }).at;
  for (let i = 0; i < last; i += 1) {
    const a = controls[i] as { weathering: number };
    const b = controls[i + 1] as { weathering: number };
    const steps = SAMPLES_PER_STRETCH / 2;
    for (let s = 0; s < steps; s += 1) {
      const t = s / steps;
      const point = normalise(catmullRom(at(i - 1), at(i), at(i + 1), at(i + 2), t));
      // Each country keeps its own water through most of its stretch and
      // hands over to the next near the border.
      const blend = smoothstep(0.3, 0.7, t);
      points.push({
        at: point,
        weathering: a.weathering + (b.weathering - a.weathering) * blend,
        along: 0,
      });
    }
  }
  const mouth = controls[last] as { at: Vec3; weathering: number };
  points.push({ at: mouth.at, weathering: mouth.weathering, along: 0 });

  /**
   * On past the end country until it reaches the sea.
   *
   * Both ends run out to the coast, not just the mouth. A river that began in
   * the middle of a continent looked like water appearing from nothing; open
   * at both ends it reads as what it is — the ocean coming in at one shore,
   * through every prayer's country, and out at the other.
   */
  const toSea = (from: Vec3, towards: Vec3, weathering: number): CoursePoint[] => {
    if (!continent) return [];
    const step = sub(from, towards);
    const tangent = normalise(sub(step, scale(from, dot(step, from))));
    const stride = scale(tangent, 0.004);
    const run: CoursePoint[] = [];
    let here = from;
    for (let i = 0; i < 260; i += 1) {
      here = normalise(add(here, stride));
      run.push({ at: here, weathering, along: 0 });
      // One step past the coastline, so the water plainly joins the sea
      // rather than stopping exactly on the line.
      if (!containsPoint(continent.ring, here)) break;
    }
    return run;
  };

  const spring = controls[0] as { at: Vec3; weathering: number };
  const head = toSea(spring.at, at(1), spring.weathering).reverse();
  const tail = toSea(mouth.at, at(last - 1), mouth.weathering);
  const whole = [...head, ...points, ...tail];

  // Held ashore, except the few steps at either end where it meets the sea.
  const kept = continent
    ? whole.map((p, i) =>
        i < head.length + 1 || i >= head.length + points.length - 1
          ? p
          : { ...p, at: keepInside(p.at, continent) },
      )
    : whole;
  kept.forEach((p, i) => {
    p.along = i / (kept.length - 1);
  });
  return kept;
}

function catmullRom(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, t: number): Vec3 {
  const t2 = t * t;
  const t3 = t2 * t;
  const f = (a: number, b: number, c: number, d: number) =>
    0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  return { x: f(p0.x, p1.x, p2.x, p3.x), y: f(p0.y, p1.y, p2.y, p3.y), z: f(p0.z, p1.z, p2.z, p3.z) };
}

function smoothstep(lo: number, hi: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
}

/** A point off the coast is drawn back towards the middle of the continent until it is well ashore. */
function keepInside(point: Vec3, continent: Territory): Vec3 {
  if (containsPoint(continent.ring, point)) return point;
  const centre = normalise(continent.site);
  let inside = 0;
  let outside = 1;
  for (let i = 0; i < 16; i += 1) {
    const mid = (inside + outside) / 2;
    if (containsPoint(continent.ring, slerp(centre, point, mid))) inside = mid;
    else outside = mid;
  }
  return slerp(centre, point, inside * 0.85);
}

/**
 * A flat strip laid along the course. `across` runs 0..1 bank to bank, `down`
 * is distance downstream in river widths, so the water's pattern keeps its
 * proportions however long the stretch.
 */
function ribbon(course: CoursePoint[], radius: number, widen: number): BufferGeometry {
  const n = course.length;
  const positions = new Float32Array(n * 2 * 3);
  const normals = new Float32Array(n * 2 * 3);
  const flow = new Float32Array(n * 2 * 2);
  const health = new Float32Array(n * 2);
  let down = 0;

  for (let i = 0; i < n; i += 1) {
    const here = course[i] as CoursePoint;
    const ahead = (course[Math.min(n - 1, i + 1)] as CoursePoint).at;
    const behind = (course[Math.max(0, i - 1)] as CoursePoint).at;
    const up = here.at;
    const side = normalise(cross(up, sub(ahead, behind)));
    // Both ends are open sea now, so there is no spring to taper from: the
    // water is at its widest where it meets the ocean at either coast and
    // narrows as it runs inland.
    const toMouth = Math.min(here.along, 1 - here.along);
    const half = HALF_WIDTH * widen * (1.2 - 0.4 * smoothstep(0, 0.14, toMouth));
    if (i > 0) {
      const prev = (course[i - 1] as CoursePoint).at;
      down += Math.sqrt(dot(sub(here.at, prev), sub(here.at, prev))) / (HALF_WIDTH * 2);
    }
    for (let k = 0; k < 2; k += 1) {
      const edge = normalise(add(up, scale(side, k === 0 ? -half : half)));
      const v = i * 2 + k;
      positions.set([edge.x * radius, edge.y * radius, edge.z * radius], v * 3);
      normals.set([edge.x, edge.y, edge.z], v * 3);
      flow.set([k, down], v * 2);
      health[v] = here.weathering;
    }
  }

  const index: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const a = i * 2;
    index.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('aFlow', new BufferAttribute(flow, 2));
  geometry.setAttribute('aHealth', new BufferAttribute(health, 1));
  geometry.setIndex(index);
  return geometry;
}

/**
 * A matte lit material, with its colour and glow worked out per pixel from how healthy the water is there: clear blue with running light on
 * it, through brown silt, to a still acid green with scum and bubbles that
 * rise and burst.
 */
function riverMaterial() {
  const time = { value: 0 };
  // Matte: the camera looks along the ground close in, and at that angle any
  // gloss turns the water into a grey mirror of the sky whatever its colour.
  // The light on healthy water is painted into its colour instead.
  const material = new MeshLambertMaterial({ side: DoubleSide });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute vec2 aFlow;
attribute float aHealth;
varying vec2 vFlow;
varying float vHealth;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vFlow = aFlow;
vHealth = aHealth;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uTime;
varying vec2 vFlow;
varying float vHealth;
float rHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float rNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(rHash(i), rHash(i + vec2(1.0, 0.0)), u.x), mix(rHash(i + vec2(0.0, 1.0)), rHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float rFbm(vec2 p) { float v = 0.0; float a = 0.5; for (int i = 0; i < 4; i++) { v += a * rNoise(p); p *= 2.03; a *= 0.5; } return v; }
// Bubbles on a grid drifting with the water: each swells, then bursts, on its own clock.
float rBubbles(vec2 p, float density) {
  vec2 cell = floor(p); vec2 f = fract(p);
  float seed = rHash(cell);
  if (seed > density) return 0.0;
  float life = fract(uTime * (0.25 + 0.3 * rHash(cell + 7.0)) + seed * 9.0);
  vec2 centre = vec2(0.25 + 0.5 * rHash(cell + 3.0), 0.25 + 0.5 * rHash(cell + 5.0));
  float r = 0.08 + 0.22 * life;
  float d = length(f - centre);
  float ring = smoothstep(r, r - 0.05, d) - smoothstep(r - 0.06, r - 0.14, d) * 0.6;
  return ring * smoothstep(1.0, 0.85, life);
}
vec3 gRiverColour; float gRiverGlow;`,
      )
      .replace(
        'vec4 diffuseColor = vec4( diffuse, opacity );',
        `{
  float h = clamp(vHealth, 0.0, 1.0);
  // Kept water runs; neglected water barely moves.
  float speed = mix(0.9, 0.05, smoothstep(0.0, 0.8, h));
  vec2 p = vec2(vFlow.x * 2.0, vFlow.y * 0.6 - uTime * speed);
  float ripple = rFbm(p * vec2(2.5, 1.0));
  float silt = rFbm(vec2(vFlow.x * 3.0, vFlow.y * 0.8 - uTime * speed * 0.5) * 1.7);

  vec3 clear = mix(vec3(0.10, 0.45, 0.72), vec3(0.42, 0.82, 0.95), ripple);
  float sparkle = smoothstep(0.66, 0.74, ripple) * (1.0 - smoothstep(0.1, 0.4, h));
  clear += sparkle * 0.5;
  vec3 mud = mix(vec3(0.30, 0.24, 0.13), vec3(0.42, 0.34, 0.18), silt);
  vec3 poison = mix(vec3(0.06, 0.09, 0.03), vec3(0.20, 0.30, 0.04), silt);
  // Scum gathers on still, poisoned water.
  float scum = smoothstep(0.55, 0.7, rFbm(vec2(vFlow.x * 4.0, vFlow.y * 1.2 - uTime * 0.02) * 1.3)) * smoothstep(0.55, 0.9, h);
  poison = mix(poison, vec3(0.45, 0.52, 0.12), scum * 0.8);

  vec3 colour = h < 0.45 ? mix(clear, mud, smoothstep(0.0, 0.45, h)) : mix(mud, poison, smoothstep(0.45, 0.85, h));
  // Square cells: across is 0..1 over one width and down is in widths.
  float bubbles = rBubbles(vec2(vFlow.x * 2.5, vFlow.y * 2.5 - uTime * speed * 1.5), smoothstep(0.45, 1.0, h) * 0.8);
  colour = mix(colour, vec3(0.62, 0.90, 0.22), bubbles);
  // Darker toward the banks, as deeper water is lighter in the middle.
  colour *= 0.72 + 0.28 * sin(vFlow.x * 3.14159);

  gRiverColour = colour;
  gRiverGlow = bubbles * 0.7 + smoothstep(0.7, 1.0, h) * 0.06;
}
vec4 diffuseColor = vec4( gRiverColour, opacity );`,
      )
      .replace(
        'vec3 totalEmissiveRadiance = emissive;',
        'vec3 totalEmissiveRadiance = vec3(0.45, 0.85, 0.15) * gRiverGlow;',
      );
  };
  return { material, time };
}
